import { Star, Quote, ArrowRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

import bsiLogo from "@/assets/logos/vvb/bsi.png";
import controlUnionLogo from "@/assets/logos/vvb/control-union.png";
import tuvNordLogo from "@/assets/logos/vvb/tuv-nord-new.png";
import bureauVeritasLogo from "@/assets/logos/vvb/bureau-veritas.png";
import pwcLogo from "@/assets/logos/vvb/pwc.png";
import sgsLogo from "@/assets/logos/vvb/sgs.png";
import tuvRheinlandLogo from "@/assets/logos/vvb/tuv-rheinland.png";
import testimonialChutikarn from "@/assets/testimonial-chutikarn.jpg";
import testimonialJason from "@/assets/testimonial-jason.jpg";

const vvbLogos = [
  { name: "PwC - Carbonwize verification and validation partner", src: pwcLogo },
  { name: "TÜV NORD - Carbon audit certification body partner", src: tuvNordLogo },
  { name: "Bureau Veritas - GHG verification partner", src: bureauVeritasLogo },
  { name: "BSI - Carbon footprint certification partner", src: bsiLogo },
  { name: "TÜV Rheinland - ISO 14064 verification partner", src: tuvRheinlandLogo },
  { name: "Control Union - Sustainability certification partner", src: controlUnionLogo },
  { name: "SGS - Carbon emissions verification partner", src: sgsLogo },
];

const TestimonialsSection = () => {
  const { t } = useLanguage();

  const testimonials = [
    {
      quote: t("testimonials.quote2"),
      author: t("testimonials.author2"),
      role: t("testimonials.role2"),
      company: t("testimonials.company2"),
      rating: 5,
      initials: "CT",
      avatar: testimonialChutikarn
    },
    {
      quote: t("testimonials.quote3"),
      author: t("testimonials.author3"),
      role: t("testimonials.role3"),
      company: t("testimonials.company3"),
      rating: 5,
      initials: "JL",
      avatar: testimonialJason
    },
    {
      quote: t("testimonials.quote1"),
      author: t("testimonials.author1"),
      role: t("testimonials.role1"),
      company: t("testimonials.company1"),
      rating: 5,
      initials: "PK",
      avatar: ""
    }
  ];

  return (
    <section id="testimonials" className="py-12 lg:py-16 px-2 bg-background relative overflow-hidden">
      {/* Background */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[600px] bg-gradient-glow opacity-20" />

      <div className="container mx-auto max-w-7xl relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-cta mb-6">
            {t("testimonials.title1")}
            <br />
            <span className="text-gradient">{t("testimonials.title2")}</span>
          </h2>
        </div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {testimonials.map((testimonial, index) => (
            <div
              key={index}
              className="group relative p-6 lg:p-8 rounded-2xl bg-card border border-border/50 shadow-soft hover:shadow-card transition-all duration-300 hover:-translate-y-1"
              style={{ animationDelay: `${index * 150}ms` }}
            >
              {/* Quote Icon */}
              <Quote className="w-10 h-10 text-primary/20 mb-4" />

              {/* Rating */}
              <div className="flex gap-1 mb-4">
                {Array.from({ length: testimonial.rating }).map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-accent text-accent" />
                ))}
              </div>

              {/* Quote */}
              <p className="text-foreground/90 leading-relaxed mb-6">"{testimonial.quote}"</p>

              {/* Author */}
              <div className="border-t border-border/50 pt-4 flex items-center gap-3">
                <Avatar className="w-12 h-12 border-2 border-primary/20">
                  <AvatarImage src={testimonial.avatar} alt={testimonial.author} />
                  <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                    {testimonial.initials}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold text-foreground">{testimonial.author}</p>
                  <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                  <p className="text-sm text-primary">{testimonial.company}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Social Proof CTA */}
        <div className="text-center mt-12 p-8 rounded-2xl bg-gradient-to-r from-primary/5 to-accent/5 border border-border/50">
          <p className="text-xl font-semibold text-foreground mb-2">
            {t("testimonials.ctaTitle")}
          </p>
          <p className="text-muted-foreground mb-6">
            {t("testimonials.ctaDescription")}
          </p>
          <Link to="/contact-us">
            <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 group">
              {t("testimonials.ctaButton")}
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>

        {/* VVB Logo Ticker */}
        <div id="vvb-auditors" className="border-t border-border/50 pt-12 overflow-hidden">
          <h3 className="text-center text-xl sm:text-2xl font-semibold text-muted-foreground tracking-wide mb-8">
            {t("testimonials.vvbTitle")}
          </h3>
          <div className="relative">
            <div className="flex animate-scroll-left">
              {/* First set of logos */}
              {vvbLogos.map((logo, index) => (
                <div
                  key={`first-${index}`}
                  className="flex-shrink-0 mx-6 sm:mx-12 flex items-center justify-center"
                >
                  <img 
                    src={logo.src} 
                    alt={logo.name}
                    className="h-8 sm:h-12 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity duration-300"
                    loading="lazy"
                    width={96}
                    height={48}
                  />
                </div>
              ))}
              {/* Duplicate set for seamless loop */}
              {vvbLogos.map((logo, index) => (
                <div
                  key={`second-${index}`}
                  className="flex-shrink-0 mx-6 sm:mx-12 flex items-center justify-center"
                >
                  <img 
                    src={logo.src} 
                    alt={logo.name}
                    className="h-8 sm:h-12 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity duration-300"
                    loading="lazy"
                    width={96}
                    height={48}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
